/**
 * DcImport（DC Keeper 標準範本匯入）契約。
 *
 * 後端 `POST DcImport/ImportDcKeeperHardware`／`ImportDcKeeperSoftware` 收 multipart `file`
 * （DC Keeper 標準範本 xlsx）回 `ImportResultDto`。前端不解析、不重組、不驗證欄位 ——
 * 標題對欄位是後端的事，前端只把檔案送過去、把結果講清楚。
 *
 * 標題列逐字照 Swagger 對四支 endpoint 的欄序描述 ——
 * `helpers/__tests__/importContract.test.js` 直接讀 swagger 檔比對，後端改欄序時測試會紅。
 */

/** 機房標準範本標題列（5 欄）。`key` 只給 mock 與測試用，後端不看。 */
export const DCKEEPER_ROOM_COLUMNS = [
  { key: "room_name", label: "機房名稱" },
  { key: "address", label: "地址" },
  { key: "floor", label: "樓層" },
  { key: "area", label: "區域" },
  { key: "manager", label: "管理者" },
];

/** 機櫃標準範本標題列（20 欄）。 */
export const DCKEEPER_RACK_COLUMNS = [
  { key: "room_name", label: "機房名稱" },
  { key: "floor", label: "樓層" },
  { key: "area", label: "區域" },
  { key: "business_category", label: "業務類別" },
  { key: "key_business", label: "關鍵業務" },
  { key: "asset_name", label: "資產名稱(Host Name)" },
  { key: "asset_category", label: "資產類別" },
  { key: "property_no", label: "財產編號" },
  { key: "brand", label: "廠牌" },
  { key: "model", label: "型號" },
  { key: "rack_no", label: "機櫃編號" },
  { key: "power_circuit", label: "電源迴路" },
  { key: "voltage", label: "電壓" },
  { key: "capacity_kva", label: "容量KVA" },
  { key: "left_power_count", label: "左電源數" },
  { key: "right_power_count", label: "右電源數" },
  { key: "pdu_count", label: "PDU數" },
  { key: "total_u", label: "總U數" },
  { key: "mounted_date", label: "上架日期" },
  { key: "manager", label: "管理者" },
];

/**
 * 硬體標準範本標題列（15 欄）。`key` 只給 mock 與測試用，後端不看。
 * 2026-09-14 swagger 由 12 欄改 15 欄：「機櫃位置含U」拆成「機櫃」「U位」，並新增 EOS／EOL；
 * 2026-09-24 SIT 拿掉 EOS／EOL → 13 欄；2026-09-29 SIT 又把 EOS／EOL 加回，排在「硬體管理者」之後。
 * 欄序以 swagger 為準（importContract.test.js 逐欄比對）。
 */
export const DCKEEPER_HARDWARE_COLUMNS = [
  { key: "asset_no", label: "資產編號" },
  { key: "key_business", label: "業務類別（關鍵業務）" },
  { key: "host_name", label: "硬體資產名稱" },
  { key: "brand", label: "廠牌" },
  { key: "model", label: "型號" },
  { key: "serial_no", label: "序號" },
  { key: "firmware_version", label: "韌體版本" },
  { key: "project_name", label: "專案名稱" },
  { key: "room", label: "機房" },
  { key: "rack_no", label: "機櫃" },
  { key: "position_u", label: "U位" },
  { key: "ip_address", label: "IP Address" },
  { key: "manager", label: "硬體管理者" },
  { key: "eos_date", label: "EOS" },
  { key: "eol_date", label: "EOL" },
];

/**
 * 軟體標準範本標題列（8 欄；2026-09-29 SIT 在「管理者」之後加回 EOS／EOL）。
 * 安裝主機與軟體名稱是兩欄，不要「幫忙」合併。
 */
export const DCKEEPER_SOFTWARE_COLUMNS = [
  { key: "asset_no", label: "資產編號" },
  { key: "key_business", label: "業務類別(Y)" },
  { key: "installed_host_name", label: "硬體資產名稱" },
  { key: "software_name", label: "軟體資產名稱" },
  { key: "version", label: "軟體版本" },
  { key: "manager", label: "管理者" },
  { key: "eos_date", label: "EOS" },
  { key: "eol_date", label: "EOL" },
];

/**
 * `createMissingReferences`：查無機房／機櫃／廠牌／型號／專案時要不要自動建檔。
 *
 * swagger 預設值就是 **true**（2026-09-09 確認；軟體端點註明「保留參數，本範本無需自動建檔之參照」）。
 * 樣本裡機房寫「15」、機櫃寫「2H02」，對不到就由後端自動建檔；要改政策改這一個常數就好。
 */
export const CREATE_MISSING_REFERENCES = true;

const int = (v) => (Number.isFinite(Number(v)) && v !== null && v !== "" ? Number(v) : 0);
const lines = (list) => (Array.isArray(list) ? list : [])
  .map((e) => ({ row: int(e && e.row_number), reason: (e && e.message) || "" }));

/**
 * ImportResultDto → 結果畫面。
 * `success`／`replaced` 是舊版（前端解析式匯入）結果畫面的鍵名，留別名讓元件不用分兩套。
 */
export function importResultToView(dto = {}) {
  const d = dto || {};
  const inserted = int(d.inserted_count);
  const updated = int(d.updated_count);
  return {
    total: int(d.total_rows),
    inserted,
    updated,
    // 後端的成功數自己給(success_count);缺時退回 新增＋更新
    successCount: d.success_count == null ? inserted + updated : int(d.success_count),
    failedCount: int(d.failure_count),
    success: inserted,
    replaced: updated,
    rows: lines(d.errors),
    warnings: lines(d.warnings),
  };
}
